import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref } from 'vue';
import Spinner from '@/components/Spinner.vue';
import TrashcanIcon from '@/assets/images/svg/trashcan.svg?component';
import {
  successNotification,
  failNotification,
} from '@/services/notificationService/notificationService';
import { getMaskedValue } from '@/utils/maskFieldValue';
import { buildScannerCredentialFieldsPayload } from '@/utils/buildScannerCredentialFieldsPayload';
import { useFetchScannerCredentialFields } from '@/_core/entities/scanner-credential-fields/api/useFetchScannerCredentialFields';
import { useUpdateScannerCredentialFields } from '@/_core/entities/scanner-credential-fields/api/useUpdateScannerCredentialFields';
import {
  CUSTOM_FIELD_OPTION,
  DEFAULT_SCANNER_CREDENTIAL_FIELDS_DISABLED_REASON,
  DEFAULT_SCANNER_CREDENTIAL_FIELDS_MODE,
  DEFAULT_SCANNER_CREDENTIAL_FIELDS_TITLE,
} from '@/_core/entities/scanner-credential-fields/models/constants';
import type {
  TNormalizedScannerCredentialField,
  TScannerCredentialFieldsMode,
  TScannerCredentialFieldsQueryPayload,
  TScannerCredentialFieldsUpdatePayload,
} from '@/_core/entities/scanner-credential-fields/models/types';
import ScannerCredentialFieldsAddFieldModal from '@/_core/entities/scanner-credential-fields/ui/ScannerCredentialFieldsAddFieldModal.vue';

type TScannerCredentialFieldsProps = {
  mode?: TScannerCredentialFieldsMode;
  integrationIdProp?: string;
  disabled?: boolean;
  disabledReason?: string;
  title?: string;
  hideTitle?: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ScannerCredentialFields',
  props: {
    mode: null,
    integrationIdProp: null,
    disabled: { type: Boolean },
    disabledReason: null,
    title: null,
    hideTitle: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  mode?: TScannerCredentialFieldsMode;
  integrationIdProp?: string;
  disabled?: boolean;
  disabledReason?: string;
  title?: string;
  hideTitle?: boolean;
};



const instance = getCurrentInstance();

const isAddFieldModalOpen = ref(false);
const isRemoveAllConfirmModalOpen = ref(false);
const isDisabledHintVisible = ref(false);

const queryPayload = computed<TScannerCredentialFieldsQueryPayload | null>(() => {
  if ((props.mode ?? DEFAULT_SCANNER_CREDENTIAL_FIELDS_MODE) === 'single-scan') {
    return { mode: 'single-scan' };
  }

  const routeIntegrationId = instance?.proxy?.$route?.params?.id;
  const integrationIdFromRoute = typeof routeIntegrationId === 'string' ? routeIntegrationId : '';
  const integrationId = props.integrationIdProp || integrationIdFromRoute;

  if (!integrationId) {
    return null;
  }

  return {
    mode: 'integration',
    integrationId,
  };
});

const { data: fieldsData, isLoading: isLoadingFields } =
  useFetchScannerCredentialFields(queryPayload);

const { mutateAsync: updateFieldsMutation, isPending: isUpdatingFields } =
  useUpdateScannerCredentialFields();

const fields = computed<TNormalizedScannerCredentialField[]>(() => fieldsData.value ?? []);

const displayedFields = computed<TNormalizedScannerCredentialField[]>(() =>
  fields.value.filter((field: TNormalizedScannerCredentialField) => !!field.value)
);

const showSpinner = computed(() => isLoadingFields.value || isUpdatingFields.value);

const isDisabled = computed(() => props.disabled ?? false);
const shouldShowTitle = computed(() => !props.hideTitle);

const resolvedTitle = computed(() => props.title ?? DEFAULT_SCANNER_CREDENTIAL_FIELDS_TITLE);

const resolvedDisabledReason = computed(() => {
  if (props.disabledReason) {
    return props.disabledReason;
  }

  return DEFAULT_SCANNER_CREDENTIAL_FIELDS_DISABLED_REASON;
});

function openAddFieldModal(): void {
  if (isDisabled.value) {
    return;
  }

  isAddFieldModalOpen.value = true;
}

function onDisabledBlockHover(isHovered: boolean): void {
  if (!isDisabled.value) {
    isDisabledHintVisible.value = false;
    return;
  }

  isDisabledHintVisible.value = isHovered;
}

function closeAddFieldModal(): void {
  isAddFieldModalOpen.value = false;
}

function openRemoveAllConfirmModal(): void {
  if (!displayedFields.value.length) {
    return;
  }

  isRemoveAllConfirmModalOpen.value = true;
}

function closeRemoveAllConfirmModal(): void {
  isRemoveAllConfirmModalOpen.value = false;
}

async function updateFields(fields: TNormalizedScannerCredentialField[]): Promise<boolean> {
  if (isDisabled.value) {
    return false;
  }

  if (showSpinner.value) {
    return false;
  }

  const payloadBase = queryPayload.value;

  if (!payloadBase) {
    return false;
  }

  const payload: TScannerCredentialFieldsUpdatePayload = {
    ...payloadBase,
    scannerCredentialFields: buildScannerCredentialFieldsPayload(
      fields,
      CUSTOM_FIELD_OPTION.fieldKey
    ),
  };

  try {
    await updateFieldsMutation(payload);
    successNotification.call(instance?.proxy, 'settingsUpdated');
    return true;
  } catch {
    failNotification.call(instance?.proxy);
    return false;
  }
}

async function submitField(field: TNormalizedScannerCredentialField): Promise<boolean> {
  const nextFields = [...displayedFields.value, field];

  return updateFields(nextFields);
}

async function removeField(fieldKey: string): Promise<void> {
  const nextFields = displayedFields.value.filter(
    (field: TNormalizedScannerCredentialField) => field.fieldKey !== fieldKey
  );

  if (nextFields.length === displayedFields.value.length) {
    return;
  }

  await updateFields(nextFields);
}

async function removeAllFields(): Promise<void> {
  if (!displayedFields.value.length) {
    return;
  }

  const isUpdated = await updateFields([]);

  if (!isUpdated) {
    return;
  }

  closeRemoveAllConfirmModal();
}

return { __sfc: true,props, instance, isAddFieldModalOpen, isRemoveAllConfirmModalOpen, isDisabledHintVisible, queryPayload, fieldsData, isLoadingFields, updateFieldsMutation, isUpdatingFields, fields, displayedFields, showSpinner, isDisabled, shouldShowTitle, resolvedTitle, resolvedDisabledReason, openAddFieldModal, onDisabledBlockHover, closeAddFieldModal, openRemoveAllConfirmModal, closeRemoveAllConfirmModal, updateFields, submitField, removeField, removeAllFields, Spinner, TrashcanIcon, getMaskedValue, ScannerCredentialFieldsAddFieldModal }
}

})